import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import { useApolloClient } from '@apollo/client';
import type { NormalizedCacheObject, ApolloClient as ApolloClientType } from '@apollo/client';
import type { UIMessage } from 'ai';
import { useTranslation } from 'react-i18next';
import { AndieAgents } from '@/ai/enum/andie-agents.enum';
import { ApolloChatTransport } from '@/ai/transport/apollo-chat-transport';
import { AndieChatThreadsDocument } from '@/graphql/generated/andie-history.generated';
import { AndieUsageDocument } from '@/graphql/generated/andie-usage.generated';

const ANDIE_HANDOFF_TRIGGER = '<hidden-message>continue with the handoff</hidden-message>';

const ANDIE_MODE_STORAGE_KEY = 'andie-chat-mode';

// Main chat is always harnessed. Guided is the default: only an explicit stored 'auto' opts out,
// so first-time / never-toggled users get 'guided' while users who chose 'auto' keep it.
const readStoredMode = (): string => {
    try {
        return localStorage.getItem(ANDIE_MODE_STORAGE_KEY) === 'auto' ? 'auto' : 'guided';
    } catch {
        return 'guided';
    }
};

export type HarnessStepInfo = {
    stepIndex: number;
    agentId: string;
    task: string;
    expectedResult?: string;
    status?: string;
};

export type HarnessPlanData = {
    runId: string;
    mode: string;
    status: string;
    cursor: number;
    steps: HarnessStepInfo[];
    /** Present when the plan is paused (status 'awaiting'); carries the pause prompt on rehydration. */
    awaiting?: HarnessAwaitingData | null;
};

export type HarnessAwaitingData = {
    reason: 'force-feedback' | 'question' | 'destructive' | 'needs-input';
    stepIndex: number;
    agentId?: string;
    task?: string;
    /** Free-text prompt for evaluator (non-askUser) pauses. */
    userQuestion?: string;
    proposedAction?: { action: string; args?: unknown };
    /**
     * For 'needs-input' askUser pauses: 1-4 questions, each with prewritten options (a short subject
     * and an optional longer description). Mirrors Claude Code's AskUserQuestion tool. Rendered by
     * AndieAskUserPrompt as a single-question wizard.
     */
    userQuestions?: Array<{
        question: string;
        header?: string;
        options: Array<{ subject: string; description?: string }>;
        allowMultiple: boolean;
    }>;
};

type MessagesState = {
    messages: UIMessage[];
    chatId: string;
};

type ChatControls = {
    setMessages: (messages: UIMessage[] | ((messages: UIMessage[]) => UIMessage[])) => void;
    setThreadId: (id: string) => void;
    sendMessage: (text: string) => void;
    /** Starts a fresh thread (new id, clearing the current one) and optionally sends `text` into it. */
    startNewConversation: (text?: string) => void;
    triggerHandoff: () => void;
    stop: () => void;
    status: 'ready' | 'streaming' | 'submitted' | 'error';
    error?: Error;
    isLoading: boolean;
    isHydrated: boolean;
    setHydrated: (hydrated: boolean) => void;
    agentId: string;
    setAgentId: (id: string) => void;
    getMessages: () => UIMessage[];
    agentNavigationPendingRef: React.MutableRefObject<boolean>;
    mode: string | null;
    setMode: (mode: string | null) => void;
    harnessPlan: HarnessPlanData | null;
    harnessAwaiting: HarnessAwaitingData | null;
    /** Restore the plan panel from a thread's persisted plan (or null) on thread load. */
    hydrateHarnessPlan: (plan: HarnessPlanData | null) => void;
};

export type AndieChatContextType = MessagesState & ChatControls;

const MessagesContext = createContext<MessagesState | null>(null);
const ControlsContext = createContext<ChatControls | null>(null);

interface AndieChatProviderProps {
    children: React.ReactNode;
    publicMode?: boolean;
    defaultAgentId?: string;
    initialHydrated?: boolean;
}

export function AndieChatProvider({
    children,
    publicMode = false,
    defaultAgentId = AndieAgents.GENERAL,
    initialHydrated = publicMode,
}: AndieChatProviderProps) {
    const apolloClient = useApolloClient() as ApolloClientType<NormalizedCacheObject>;
    const { i18n } = useTranslation();
    const [agentId, setAgentId] = useState<string>(defaultAgentId);
    const agentIdRef = useRef(agentId);
    const messagesRef = useRef<UIMessage[]>([]);
    const [threadId, setThreadIdState] = useState<string | null>(null);
    const threadIdRef = useRef<string | null>(null);
    const [isHydrated, setHydrated] = useState(initialHydrated);
    const agentNavigationPendingRef = useRef(false);
    // Main chat is always harnessed (persisted, default 'guided'); public/onboarding has no harness mode.
    const [mode, setModeState] = useState<string | null>(publicMode ? null : readStoredMode());
    const modeRef = useRef<string | null>(mode);
    const [harnessPlan, setHarnessPlan] = useState<HarnessPlanData | null>(null);
    const [harnessAwaiting, setHarnessAwaiting] = useState<HarnessAwaitingData | null>(null);
    agentIdRef.current = agentId;
    modeRef.current = mode;

    const transport = useMemo(
        () =>
            new ApolloChatTransport({
                client: apolloClient,
                publicMode,
                getBody: () => ({
                    agentId: agentIdRef.current,
                    threadId: threadIdRef.current,
                    mode: modeRef.current ?? undefined,
                }),
            }),
        [apolloClient, publicMode],
    );
    transport.setUserLanguageGetter(() => i18n.language?.trim() || undefined);

    const { messages, setMessages, sendMessage, status, stop, id, error } = useChat({
        transport,
        // Without this, the AI SDK re-renders the whole message list on EVERY streamed token. A research
        // turn (11+ heavy tool cards + a long streamed answer) then re-renders 100+ times and freezes the
        // page. Batch message/data updates to ~10/sec (matches the per-part useThrottledValue cadence).
        experimental_throttle: 100,
        onData: (dataPart) => {
            if (dataPart.type === 'data-harness-plan') {
                setHarnessPlan(dataPart.data as HarnessPlanData);
                setHarnessAwaiting(null);
            } else if (dataPart.type === 'data-harness-awaiting-confirmation') {
                setHarnessAwaiting(dataPart.data as HarnessAwaitingData);
            } else if (dataPart.type === 'data-andie-thread-title') {
                // The harness named this thread (first message + plan). Pull the freshly
                // titled thread into the history list so it shows immediately.
                apolloClient.refetchQueries({ include: [AndieChatThreadsDocument] }).catch(() => {});
            }
        },
    });

    messagesRef.current = messages;

    // Refresh the usage meter when a run finishes (main chat only — public/onboarding has no workspace usage).
    const prevStatusRef = useRef(status);
    useEffect(() => {
        const prev = prevStatusRef.current;
        prevStatusRef.current = status;
        if (!publicMode && prev === 'streaming' && status === 'ready') {
            apolloClient.refetchQueries({ include: [AndieUsageDocument] }).catch(() => {});
        }
    }, [status, publicMode, apolloClient]);

    const setThreadId = useCallback((nextThreadId: string) => {
        threadIdRef.current = nextThreadId;
        setThreadIdState(nextThreadId);

        // A harness plan belongs to a single thread/run; clear it when the active thread changes (new chat
        // or switching threads) so the previous thread's plan doesn't linger on the new one.
        setHarnessPlan(null);
        setHarnessAwaiting(null);
    }, []);

    // Rehydrate the plan panel from the persisted plan returned by the thread-load query, so switching
    // back to an unfinished thread restores its panel (and re-renders the pause prompt if it was awaiting).
    // Call AFTER setThreadId, which clears the panel first. Null leaves it cleared.
    const hydrateHarnessPlan = useCallback((plan: HarnessPlanData | null) => {
        setHarnessPlan(plan);
        setHarnessAwaiting(plan?.status === 'awaiting' && plan.awaiting ? plan.awaiting : null);
    }, []);

    const setMode = useCallback(
        (nextMode: string | null) => {
            setModeState(nextMode);
            // Only the main chat has a persistable harness mode; public/onboarding stays ephemeral.
            if (publicMode) return;
            try {
                if (nextMode) localStorage.setItem(ANDIE_MODE_STORAGE_KEY, nextMode);
                else localStorage.removeItem(ANDIE_MODE_STORAGE_KEY);
            } catch {
                // Ignore storage failures (private mode, quota, etc.).
            }
        },
        [publicMode],
    );

    if (!threadIdRef.current) {
        threadIdRef.current = id;
    }

    const getMessages = useCallback(() => messagesRef.current, []);

    const isLoading = status === 'streaming' || status === 'submitted';

    const sendTextMessage = useCallback(
        (text: string) => {
            if (!isHydrated || isLoading) return;
            // Clear any pending harness confirmation once the user replies.
            setHarnessAwaiting(null);
            sendMessage({ text });
        },
        [isHydrated, isLoading, sendMessage],
    );

    const triggerHandoff = useCallback(() => {
        if (!isHydrated) return;
        sendMessage({ text: ANDIE_HANDOFF_TRIGGER });
    }, [isHydrated, sendMessage]);

    const startNewConversation = useCallback(
        (text?: string) => {
            if (isLoading) stop();
            // A harness plan belongs to a single thread/run; clear it (and any pending confirmation)
            // so the previous thread's plan doesn't linger on the fresh one.
            setHarnessPlan(null);
            setHarnessAwaiting(null);
            setMessages([]);
            // Mint a fresh thread id client-side; the backend creates the thread under it on the
            // first message. Persistence is automatic: changing the thread id updates `chatId`,
            // which AndieChatManagement's effect writes to localStorage — that component owns the
            // workspace-scoped key, since this provider is mounted above SessionProvider.
            const nextThreadId = crypto.randomUUID();
            threadIdRef.current = nextThreadId;
            setThreadIdState(nextThreadId);
            setHydrated(true);
            // Raw sendMessage (not the hydration-guarded sendTextMessage): the thread we just
            // set is fresh and hydrated, and the transport only sends the latest message — the
            // backend rebuilds history from threadId, so no stale messages leak into the new thread.
            if (text?.trim()) sendMessage({ text });
        },
        [isLoading, stop, setMessages, sendMessage],
    );

    const messagesState = useMemo<MessagesState>(
        () => ({
            messages: messages.filter(
                (m) =>
                    !(
                        m.role === 'user' &&
                        m.parts?.length === 1 &&
                        m.parts[0].type === 'text' &&
                        (m.parts[0] as { type: 'text'; text: string }).text === ANDIE_HANDOFF_TRIGGER
                    ),
            ),
            chatId: threadId || id,
        }),
        [messages, threadId, id],
    );

    const controls = useMemo<ChatControls>(
        () => ({
            setMessages,
            setThreadId,
            sendMessage: sendTextMessage,
            startNewConversation,
            triggerHandoff,
            stop,
            status,
            error,
            isLoading,
            isHydrated,
            setHydrated,
            agentId,
            setAgentId,
            getMessages,
            agentNavigationPendingRef,
            mode,
            setMode,
            harnessPlan,
            harnessAwaiting,
            hydrateHarnessPlan,
        }),
        [
            setMessages,
            setThreadId,
            hydrateHarnessPlan,
            sendTextMessage,
            startNewConversation,
            triggerHandoff,
            stop,
            status,
            error,
            isLoading,
            isHydrated,
            setHydrated,
            agentId,
            getMessages,
            agentNavigationPendingRef,
            mode,
            setMode,
            harnessPlan,
            harnessAwaiting,
        ],
    );

    return (
        <ControlsContext.Provider value={controls}>
            <MessagesContext.Provider value={messagesState}>{children}</MessagesContext.Provider>
        </ControlsContext.Provider>
    );
}

/**
 * Messages + chatId. Components using this re-render on every streaming chunk.
 * Only use in components that actually display messages.
 */
export function useAndieChatMessages(): MessagesState {
    const ctx = useContext(MessagesContext);
    if (!ctx) throw new Error('useAndieChatMessages must be used within AndieChatProvider');
    return ctx;
}

/**
 * Controls: send, stop, setMessages, status, isLoading, agentId.
 * Does NOT re-render on streaming chunks — only on status transitions.
 */
export function useAndieChatControls(): ChatControls {
    const ctx = useContext(ControlsContext);
    if (!ctx) throw new Error('useAndieChatControls must be used within AndieChatProvider');
    return ctx;
}

/**
 * Full context — convenience for components that need everything.
 * Re-renders on every streaming chunk.
 */
export function useAndieChat(): AndieChatContextType {
    const msgs = useContext(MessagesContext);
    const ctrl = useContext(ControlsContext);
    if (!msgs || !ctrl) throw new Error('useAndieChat must be used within AndieChatProvider');
    return { ...msgs, ...ctrl };
}

/** @deprecated Use useAndieChatControls instead */
export const useAndieChatActions = useAndieChatControls;
